import { Button, ChangeEvent, ClickEvent, DataSourceMode, Image, TableRow, TableRowDisplayEvent, Textbox, TextboxProps } from "@mcleod/components";
import { AutogenLayoutTrailerPool } from "./autogen/AutogenLayoutTrailerPool";
import { CommonTooltips } from "@mcleod/common";
import { Alignment, Api, ModelRow, ModelSearchResult } from "@mcleod/core";
import { ImageName } from "@mcleod/images";

const DEFAULT_TRAILER_MIN = 1;
const DEFAULT_TRAILER_MAX = 5;
const DELIVERED = "D";

export interface TrailerPoolSearchProps {
    location_id?: string;
    include_in_prog?: boolean;
    include_preloaded_orders?: boolean;
}

export class TrailerPool extends AutogenLayoutTrailerPool {

    private controlsHidden: boolean = false;
    private statusIcons: { [key: string]: { displayName: string, image: ImageName } } = {
        "D": {displayName: "Delivered", image: ImageName.loadDelivery},
        "V": {displayName: "Voided", image: ImageName.loadVoided},
        "P": {displayName: "In Progress", image: ImageName.loadInProgress},
        "A": {displayName: "Available", image: ImageName.loadAvailable},
        "C": {displayName: "Covered", image: ImageName.loadCovered}
    };

    onLoad(){
        this.textboxTrailerPoolMin.addlValidationCallback = (_value: string, mode: DataSourceMode) => {
            return this.poolMinValidator(mode);
        };
        this.textboxTrailerPoolSize.addlValidationCallback = (_value: string, mode: DataSourceMode) => {
            return this.poolSizeValidator(mode);
        };
    }


    sourceLocationAfterExecution(){
        const is_active = this.mainDataSource.activeRow?.getBoolean("is_trailer_pool");
        if (!this.controlsHidden) {
            this.setupActivePool(is_active);
        }
        this.searchTrailerPool();
    }

    private poolMinValidator(mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const min = this.textboxTrailerPoolMin.getDataValue();
        const max = this.textboxTrailerPoolSize.getDataValue();
        if(min > max){
            return {
                component: this.textboxTrailerPoolMin,
                isValid: false,
                validationMessage: "Minimun Trailers cannot be greater than Maximum Trailers."
            };
        }
    }
    private poolSizeValidator(mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const min = this.textboxTrailerPoolMin.getDataValue();
        const max = this.textboxTrailerPoolSize.getDataValue();
        if(max < min){
            return {
                component: this.textboxTrailerPoolSize,
                isValid: false,
                validationMessage: "Maximun Trailers cannot be less than Minimum Trailers."
            };
        }
    }

    public searchTrailerPool(props?: TrailerPoolSearchProps) {
        this.tableTrailers.busy = true;

        const location_id = props?.location_id ?? this.mainDataSource.activeRow?.get("id");
        const include_in_prog = props?.include_in_prog ?? this.switchIncludeInProg.checked;
        const include_preloaded_orders = props?.include_preloaded_orders ?? true;

        this.sourceTrailerPool.search({
            location_id: location_id,
            include_in_prog: include_in_prog,
            include_preloaded_orders: include_preloaded_orders
        }).then(response => {
            if (!this.controlsHidden) {
                this.setupBalance();
                this.setupAvgIdle(response);
            }
        }).finally(() => {
            this.tableTrailers.busy = false;
        });
}

    private tableTrailersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow: ModelRow = tableRow.data;

        this.setupQuickInfo(tableRow, modelRow);
        this.setupIcons(tableRow, modelRow);
        this.setupPreloadedOrders(tableRow, modelRow);
    }

    setupQuickInfo(tableRow: TableRow, modelRow: ModelRow){
        const textBoxTrailer = tableRow.findComponentById("textboxTrailerId") as Textbox;
        CommonTooltips.setTooltipContentLayout(textBoxTrailer, modelRow.get("trailer_id"), "lme/dispatch/asset/TrailerPoolQuickInfo");

        const textBoxTractor= tableRow.findComponentById("textboxTractor") as Textbox;
        CommonTooltips.setTooltipContentLayout(textBoxTractor, modelRow.get("tractor"), "lme/dispatch/asset/TractorQuickInfo");

        const textBoxDriver= tableRow.findComponentById("textboxDriverFullName") as Textbox;
        CommonTooltips.setTooltipContentLayout(textBoxDriver, modelRow.get("driver_id"), "lme/dispatch/asset/DriverQuickInfo");

        const imageIdle30= tableRow.findComponentById("imageIdle30") as Image;
        CommonTooltips.setTooltipContentLayout(imageIdle30,modelRow,"lme/dispatch/asset/TrailerPoolIdleQuickInfo");

        const imageIdle10= tableRow.findComponentById("imageIdle10") as Image;
        CommonTooltips.setTooltipContentLayout(imageIdle10,modelRow,"lme/dispatch/asset/TrailerPoolIdleQuickInfo");

    }

    setupIcons(tableRow: TableRow, modelRow: ModelRow) {
        // Idle Days icon(s)
        const idleDays = modelRow.get("days_idle", 0);
        const imageIdle10 = tableRow.findComponentById("imageIdle10") as Image;
        const imageIdle30 = tableRow.findComponentById("imageIdle30") as Image;
        imageIdle10.visible = idleDays >= 10 && idleDays < 30;
        imageIdle30.visible = idleDays >= 30;

        // Radius icon
        const radius: number = this.mainDataSource.activeRow?.getNumber("trailer_pool_radius", 0) || 0;
        const distance: number = modelRow.get("distance",0);
        const imageRadius = tableRow.findComponentById("imageRadius") as Image;
        imageRadius.visible = distance > radius;

        // Status icon(s)
        const imageStatus: Image = tableRow.findComponentById("imageStatus") as Image;
        const status = modelRow.get("status");
        imageStatus.tooltip = this.statusIcons[status].displayName;
        imageStatus.name = this.statusIcons[status].image || null;
        imageStatus.fill = "primary";
    }

    setupPreloadedOrders(tableRow: TableRow, modelRow: ModelRow) {
        const orders = modelRow.get("preloaded_orders");

        const textBoxPreloadedOrder= tableRow.findComponentById("textboxPreloadedOrders") as Textbox;

        if(orders == undefined || orders.length == 0){
            return;
        }
        else if(orders.length == 1){
            const order = orders[0].id;
            textBoxPreloadedOrder.text = order;
        }
        else{
            const order = orders[0].id;
            textBoxPreloadedOrder.text = order + ", ...";

            const TOOLTIP_TEXTBOXPROPS: Partial<TextboxProps> = {
                printable: true,
                captionVisible: false
            }
            CommonTooltips.setTooltipContentTextboxes(
                textBoxPreloadedOrder,
                () => orders.map(element => ({...TOOLTIP_TEXTBOXPROPS, text: element.id}))
            );
        }
    }

    setupBalance(){
        const trailerCount=this.sourceTrailerPool.originalRowCount;
        this.textboxTotalTrailers.text = trailerCount.toString();
        const trailerMin = this.mainDataSource.activeRow.get("trailer_pool_min",DEFAULT_TRAILER_MIN);
        const trailerMax = this.mainDataSource.activeRow.get("trailer_pool_size",DEFAULT_TRAILER_MAX);
        let trailerBalance:Number = 0;

        if(!this.mainDataSource.activeRow.getBoolean("is_trailer_pool",false) && !this.switchActive.checked){
            this.textboxBalance.color = "default";
            this.textboxBalance.text = "";
            return;
        }
        if(trailerCount < trailerMin){
            trailerBalance = trailerMin - trailerCount;
            this.textboxBalance.color = "error";
            this.textboxBalance.text = "-" + trailerBalance.toString();
        }
        else if(trailerCount > trailerMax){
            trailerBalance = trailerCount - trailerMax;
            this.textboxBalance.color = "error";
            this.textboxBalance.text = "+" + trailerBalance.toString();
        }
        else{
            this.textboxBalance.color = "default";
            this.textboxBalance.text = "In Range";
        }

    }

    setupAvgIdle(response:ModelSearchResult){
        const rows = response.modelRows;
        let rowCountToCalc = response.modelRows.length;
        if(rows.length <= 0) return;

        const totalIdle :number = rows.reduce(function (currentTotal, row){
            if(row.get("status") != DELIVERED){
                rowCountToCalc--;
                return currentTotal;
            }
            return currentTotal + row.get("days_idle")
        },0);

        this.textboxAvgIdle.text = Math.trunc(totalIdle/rowCountToCalc).toString();
    }

    setupActivePool(value:boolean){
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const trailerMin = this.mainDataSource.activeRow.get("trailer_pool_min");
            const trailerMax = this.mainDataSource.activeRow.get("trailer_pool_size");
            if(this.switchActive.checked && trailerMin == null){
                this.textboxTrailerPoolMin.text = DEFAULT_TRAILER_MIN.toString();
            }
            if(this.switchActive.checked && trailerMax == null){
                this.textboxTrailerPoolSize.text = DEFAULT_TRAILER_MAX.toString();
            }
            this.setupBalance();
        }
        this.panelActive.visible = value;
    }

    switchActiveOnChange(event:ChangeEvent){
       this.setupActivePool(event.newValue);
    }

    switchInProgressOnChange(event:ChangeEvent){
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && this.mainDataSource.mode != DataSourceMode.ADD)
            this.searchTrailerPool();
    }


    // START - Planning Comment stuff
    textboxPlanningCommentOnChange(event) {
        if (event.newValue.length > 80) {
            const textbox: Textbox = event.target as Textbox;
            textbox.showAdhocTooltip("You have exceeded the 80 character limit.", { position: Alignment.RIGHT, shaking: true, timeout: 5000 });
            if (event.oldValue.length <= 80)
                textbox.text = event.oldValue;
            else
                textbox.text = null;
        }
    }

    textboxPlanningCommentOnClick(event: ClickEvent) {
        const planningComment = event.target as Textbox;
        if (planningComment.printable !== true && document.activeElement === planningComment.getEventTarget())
            event.stopPropagation();
    }

    textboxPlanningCommentOnDblClick(event: ClickEvent) {
        this.textboxPlanningCommentOnClick(event);
    }

    buttonEditCommentOnClick(event: ClickEvent) {
        const editButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(editButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        const saveButton: Button = this.getPlanningCommentSaveButton(tableRow);
        const cancelButton: Button = this.getPlanningCommentCancelButton(tableRow);
        editButton["originalText"] = planningComment.text;
        planningComment.printable = false;
        planningComment.enabled = true;
        planningComment.focus();
        saveButton.visible = true;
        cancelButton.visible = true;
        editButton.visible = false;
    }

    async buttonSaveCommentOnClick(event: ClickEvent) {
        const saveButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(saveButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        const editButton: Button = this.getPlanningCommentEditButton(tableRow);
        const cancelButton: Button = this.getPlanningCommentCancelButton(tableRow);
        saveButton.busy = true;
        const trailerId: string = tableRow.data.get("trailer_id");
        await Api.post("lme/dispatch/asset/update-trailer-planning-comment",
            { trailer_id: trailerId, planning_comment: planningComment.text })
            .finally(() => saveButton.busy = false);
        planningComment.enabled = false;
        planningComment.printable = true;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    buttonCancelCommentOnClick(event: ClickEvent) {
        const cancelButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(cancelButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        const editButton: Button = this.getPlanningCommentEditButton(tableRow);
        const saveButton: Button = this.getPlanningCommentSaveButton(tableRow);
        planningComment.text = editButton["originalText"];
        planningComment.enabled = false;
        planningComment.printable = true;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    private getPlanningCommentTextbox(tableRow: TableRow): Textbox {
        return tableRow.findComponentById("textboxPlanningComment") as Textbox;
    }

    private getPlanningCommentEditButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonEditComment") as Button;
    }

    private getPlanningCommentSaveButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonSaveComment") as Button;
    }

    private getPlanningCommentCancelButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonCancelComment") as Button;
    }
    // END - Planning Comment stuff

    public hideControls() {
        this.panelControls.visible = false;
        this.tableTrailers.headerVisible = false;
        this.controlsHidden = true;
    }

}
